import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const inputCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-gold";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</span>
    {children}
  </label>
);

const FileField = ({ label, name, accept, multiple, files, onChange, testid, hint }) => (
  <div>
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</span>
    <label
      className="flex cursor-pointer items-center gap-3 border border-dashed border-white/20 bg-black/30 px-4 py-4 transition-colors hover:border-gold"
      data-testid={testid}
    >
      <Upload className="h-4 w-4 shrink-0 text-gold" />
      <span className="truncate text-xs text-white/55">
        {files.length > 0 ? Array.from(files).map((f) => f.name).join(", ") : hint}
      </span>
      <input type="file" name={name} accept={accept} multiple={multiple} className="hidden" onChange={(e) => onChange(e.target.files)} />
    </label>
  </div>
);

const initial = { name: "", email: "", age: "", location: "", height: "", measurements: "", languages: "", accents: "", experience: "", links: "", message: "" };

export default function Submissions() {
  const { t } = useLanguage();
  useSEO(t("nav.submissions") + " — Diamotims Agency", t("sub.intro"));
  const [division, setDivision] = useState(null);
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [resume, setResume] = useState([]);
  const [demos, setDemos] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const honeypotRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const DIVISIONS = [
    { id: "MODELS", title: t("sub.divModels"), copy: t("sub.divModelsCopy"), testid: "submissions-division-models" },
    { id: "ACTORS", title: t("sub.divActors"), copy: t("sub.divActorsCopy"), testid: "submissions-division-actors" },
    { id: "VOICE", title: t("sub.divVoice"), copy: t("sub.divVoiceCopy"), testid: "submissions-division-voice" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("division", division);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", consent ? "true" : "false");
      fd.append("website", honeypotRef.current?.value || "");
      Array.from(photos).slice(0, 5).forEach((f) => fd.append("photos", f));
      if (resume[0]) fd.append("resume", resume[0]);
      Array.from(demos).slice(0, 5).forEach((f) => fd.append("demos", f));
      await api.post("/applications", fd);
      setDone(true);
      toast.success(t("sub.successTitle"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="submissions-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("sub.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.1}>{t("sub.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("sub.title2")}</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {t("sub.intro")}
          </p>
        </FadeUp>

        {!division && !done && (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {DIVISIONS.map((d, i) => (
              <FadeUp key={d.id} delay={i * 0.1}>
                <button
                  data-testid={d.testid}
                  onClick={() => setDivision(d.id)}
                  className="group flex h-full w-full flex-col border border-white/10 bg-[#121212] p-8 text-left transition-colors duration-300 hover:border-gold/50 lg:p-10"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("home.divisionLabel")} 0{i + 1}</span>
                  <span className="mt-4 font-display text-3xl font-light uppercase tracking-tight text-white">{d.title}</span>
                  <span className="mt-3 text-sm leading-relaxed text-white/60">{d.copy}</span>
                  <span className="mt-auto inline-flex items-center gap-2 pt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 transition-colors group-hover:text-gold">
                    {t("sub.begin")} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </button>
              </FadeUp>
            ))}
          </div>
        )}

        {done && (
          <div data-testid="submissions-success-message" className="mx-auto mt-14 flex max-w-2xl flex-col items-center justify-center border border-gold/40 bg-[#121212] p-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center border border-gold text-gold">
              <Check className="h-6 w-6" />
            </div>
            <h2 className="mt-6 font-display text-3xl font-light uppercase tracking-tight text-white">{t("sub.successTitle")}</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60">{t("sub.successBody")}</p>
            <button
              data-testid="submissions-again-button"
              onClick={() => { setDone(false); setDivision(null); setForm(initial); setPhotos([]); setResume([]); setDemos([]); setConsent(false); }}
              className="mt-8 border border-white/30 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors hover:border-gold hover:text-gold"
            >
              {t("sub.again")}
            </button>
          </div>
        )}

        {division && !done && (
          <FadeUp className="mx-auto mt-14 max-w-3xl">
            <button
              data-testid="submissions-division-select"
              onClick={() => setDivision(null)}
              className="mb-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {DIVISIONS.find((d) => d.id === division)?.title} — {t("sub.changeDivision")}
            </button>
            <form data-testid="submissions-form" onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 sm:p-10">
              <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label={t("sub.name")}>
                  <input data-testid="sub-input-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} />
                </Field>
                <Field label={t("sub.email")}>
                  <input data-testid="sub-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
                </Field>
                <Field label={t("sub.age")}>
                  <input data-testid="sub-input-age" required type="number" min={13} max={99} value={form.age} onChange={set("age")} placeholder="21" className={inputCls} />
                </Field>
                <Field label={t("sub.location")}>
                  <input data-testid="sub-input-location" required value={form.location} onChange={set("location")} placeholder="Montréal" className={inputCls} />
                </Field>
                {division === "MODELS" && (
                  <>
                    <Field label={t("sub.height")}>
                      <input data-testid="sub-input-height" value={form.height} onChange={set("height")} placeholder={`5'10" / 178cm`} className={inputCls} />
                    </Field>
                    <Field label={t("sub.measurements")}>
                      <input data-testid="sub-input-measurements" value={form.measurements} onChange={set("measurements")} placeholder="Bust / Waist / Hips" className={inputCls} />
                    </Field>
                  </>
                )}
                <Field label={t("sub.languages")}>
                  <input data-testid="sub-input-languages" value={form.languages} onChange={set("languages")} placeholder="Français, English…" className={inputCls} />
                </Field>
                {division === "VOICE" && (
                  <Field label={t("sub.accents")}>
                    <input data-testid="sub-input-accents" value={form.accents} onChange={set("accents")} className={inputCls} />
                  </Field>
                )}
                {division === "ACTORS" && (
                  <div className="sm:col-span-2">
                    <Field label={t("sub.experience")}>
                      <textarea data-testid="sub-input-experience" rows={3} value={form.experience} onChange={set("experience")} className={`${inputCls} resize-none`} />
                    </Field>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Field label={division === "ACTORS" ? t("sub.linksActors") : division === "VOICE" ? t("sub.linksVoice") : t("sub.linksModels")}>
                    <input data-testid="sub-input-links" value={form.links} onChange={set("links")} placeholder="https://…" className={inputCls} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={t("sub.message")}>
                    <textarea data-testid="sub-input-message" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <FileField
                    label={division === "MODELS" ? t("sub.photosModels") : t("sub.photosOther")}
                    name="photos" accept="image/*" multiple files={photos} onChange={setPhotos} testid="sub-file-photos" hint={t("sub.fileHint")}
                  />
                </div>
                {division === "ACTORS" && (
                  <div className="sm:col-span-2">
                    <FileField label={t("sub.resume")} name="resume" accept="application/pdf" files={resume} onChange={setResume} testid="sub-file-resume" hint={t("sub.fileHint")} />
                  </div>
                )}
                {division === "VOICE" && (
                  <div className="sm:col-span-2">
                    <FileField label={t("sub.demos")} name="demos" accept="audio/*" multiple files={demos} onChange={setDemos} testid="sub-file-demos" hint={t("sub.fileHint")} />
                  </div>
                )}
              </div>

              <label className="mt-8 flex cursor-pointer items-start gap-3">
                <input data-testid="sub-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" required />
                <span className="text-xs leading-relaxed text-white/60">{t("sub.consent")}</span>
              </label>

              <button
                data-testid="submissions-submit-button"
                type="submit"
                disabled={busy || !consent}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold disabled:opacity-40"
              >
                {busy ? t("sub.submitting") : t("sub.submit")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </form>
          </FadeUp>
        )}
      </div>
    </main>
  );
}
